import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';

import { DeployPlanConfigService } from '@gfe/core/services/deploy-plan-config/deploy-plan-config.service';
import { selectDataspaceModel } from '@gfe/core/store/dataspace/dataspace.selectors';
import { isNil } from '@gfe/core/utils/shared-utils';

/**
 * How many channels a fully-deployed plan covers in the current dataspace — what tells
 * "deployed" apart from "one channel out of five".
 *
 * Provided by each component that shows a deploy status, never at the root: the read follows
 * the dataspace for as long as its injector lives, so a root instance would keep fetching the
 * channel list on every dataspace switch anywhere in the app — a request many users are not
 * allowed to make.
 */
@Injectable()
export class DeployChannelCountService {
  private readonly store = inject(Store);
  private readonly configs = inject(DeployPlanConfigService);

  /**
   * Disabled channels are excluded: one switched off cannot be deployed, so counting it
   * would put "deployed" permanently out of reach. Zero while the list is in flight and if
   * it fails, which callers read as "unknown" and fall back to judging the attempts alone —
   * a failed read must not claim a deployed plan is still deploying.
   */
  readonly enabledCount = toSignal(
    this.store.select(selectDataspaceModel).pipe(
      filter((dataspace) => !isNil(dataspace)),
      map((dataspace) => dataspace.id),
      distinctUntilChanged(),
      switchMap((id) =>
        this.configs
          .list$(id, { skipErrorNotification: true })
          .pipe(catchError(() => of({ items: [] })))
      ),
      map((response) => response.items.filter((item) => item.isEnabled).length)
    ),
    { initialValue: 0 }
  );
}
